import React, { useMemo } from 'react';
import { House, Megaphone, MessageSquare, Plane, Receipt, Users } from 'lucide-react';

import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { FINANCES_PATHS, FINANCES_NAV_MATCH_PATHS } from '@/lib/financesPaths';
import { PHASE1_FLAGS } from '@/lib/phase1Paths';

export interface PrimaryNavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Short label for the mobile tab bar, where a tab is ~90px wide. */
  shortLabel?: string;
  path?: string;
  /** Extra path prefixes that also mark this item active. */
  matchPaths?: string[];
  disabled?: boolean;
}

/**
 * The app's primary destinations, in one place.
 *
 * AR-316: the desktop rail (UnifiedSidebar) and the mobile tab bar
 * (MobileTabBar) both render from this, so a nav change lands in both shells
 * at once. Previously the list was a `useMemo` private to UnifiedSidebar.
 */
export function usePrimaryNavItems(): PrimaryNavItem[] {
  const { isEnabled: isResolvedEnabled, isLoading: flagsLoading } =
    useResolvedFeatureFlags();

  return useMemo(() => {
    // AR-445 — the Phase-1 tab bar: the four destinations used most, with the
    // rest of the navigation one tap away in the More sheet.
    if (!flagsLoading && PHASE1_FLAGS.every((f) => isResolvedEnabled(f))) {
      return [
        { icon: House, label: 'Dashboard', shortLabel: 'Home', path: '/' },
        { icon: MessageSquare, label: 'Threads', path: '/threads', matchPaths: ['/threads'] },
        { icon: Megaphone, label: 'Marketing', path: '/marketing', matchPaths: ['/marketing'] },
        { icon: Plane, label: 'Booking', path: '/booking', matchPaths: ['/booking'] },
      ];
    }

    const items: PrimaryNavItem[] = [
      {
        icon: MessageSquare,
        label: 'Threads',
        path: '/threads',
        matchPaths: ['/threads'],
      },
    ];

    if (!flagsLoading && isResolvedEnabled('invoicingMvp')) {
      items.push({
        icon: Receipt,
        label: 'Finances',
        path: FINANCES_PATHS.invoices,
        matchPaths: [...FINANCES_NAV_MATCH_PATHS],
      });
    }

    items.push({
      icon: Users,
      label: 'CRM (Soon)',
      shortLabel: 'CRM',
      disabled: true,
    });

    return items;
  }, [isResolvedEnabled, flagsLoading]);
}

/** Whether `location` sits inside the destination `item` names. */
export function isPrimaryNavItemActive(
  item: PrimaryNavItem,
  location: string,
): boolean {
  if (item.matchPaths) {
    return item.matchPaths.some(
      (p) => location === p || location.startsWith(p + '/'),
    );
  }
  if (item.path === '/') return location === '/';
  if (item.path) {
    return location === item.path || location.startsWith(item.path + '/');
  }
  return false;
}
